import * as React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  alpha,
  useTheme,
  LinearProgress,
  keyframes,
} from "@mui/material";
import {
  DescriptionOutlined,
  NoteAltOutlined,
  RuleFolder,
} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  BrainCog,
  Building,
  Building2,
  ClipboardList,
  CreditCard,
  Factory,
  FileCheck,
  Files,
  FileText,
  FolderOpen,
  House,
  LogOut,
  Package,
  Percent,
  Receipt,
  ShoppingCart,
  User,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { tokens } from "../../../theme";
import { useBranding } from "../../../BrandingContext";
import { useNavigate } from "react-router-dom";

const tooltipEnter = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const tooltipExit = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(-10px); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

interface TourStep {
  key: string;
  title: string;
  description: string;
}

const tourSteps: TourStep[] = [
  { key: "home", title: "Home", description: "Your dashboard hub. View KPIs, analytics, quick actions, and notifications at a glance." },
  { key: "patient", title: "Patient", description: "Manage your patient records, view patient details, and track treatment progress." },
  { key: "ivr", title: "IVR", description: "Submit and manage Insurance Verification Requests for your patients." },
  { key: "order", title: "Order", description: "Place and track product orders for patient treatments." },
  { key: "product", title: "Product", description: "View and manage all your invoices in one place." },
  { key: "claim", title: "Claim", description: "Submit and track insurance or reimbursement claims." },
  { key: "invoices", title: "Invoices", description: "Generate and manage billing invoices." },
  { key: "payment", title: "Payment", description: "Track payments and manage your payment history." },
  { key: "commission", title: "Service Fee", description: "View your service fee statements and earnings." },
  { key: "product_agreement", title: "Product Agreement", description: "View and manage the products you've selected to work with." },
  { key: "baa", title: "BAA", description: "Business Associate Agreements for HIPAA compliance." },
  { key: "medical_note_agreement", title: "Medical Note Agreement", description: "View and manage medical note audit agreements." },
  { key: "templates", title: "Templates", description: "Manage templates details." },
  { key: "user", title: "User", description: "Manage user accounts and permissions for your organization." },
  { key: "user_create", title: "User Create", description: "Create new user accounts for your organization." },
  { key: "practice", title: "Practice", description: "Manage your practice information and settings." },
  { key: "provider", title: "Provider", description: "Manage healthcare providers associated with your practice." },
  { key: "agency", title: "Agency", description: "View and manage agency partnerships." },
  { key: "manufacturer", title: "Manufacturer", description: "Access manufacturer information and contacts." },
  { key: "ai_medical_audit", title: "AI Medical Audit", description: "View and manage AI medical note audit records." },
  { key: "resources", title: "Resources", description: "Access and manage uploaded resources and files." },
  { key: "sign_out", title: "Sign Out", description: "Securely sign out of your account." },
];

interface SidebarChild {
  key: string;
  label: string;
  icon: React.ReactNode;
}

interface SidebarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  children?: SidebarChild[];
}

const sidebarTop: SidebarItem[] = [
  { key: "home", label: "Home", icon: <House size={18} /> },
  { key: "patient", label: "Patient", icon: <Users size={18} /> },
  { key: "ivr", label: "IVR", icon: <RuleFolder sx={{ fontSize: 18 }} /> },
  { key: "order", label: "Order", icon: <ShoppingCart size={16} /> },
  { key: "product", label: "Product", icon: <Package size={18} /> },
  { key: "claim", label: "Claim", icon: <NoteAltOutlined sx={{ fontSize: 18 }} /> },
  {
    key: "finance",
    label: "Finance",
    icon: <Receipt size={18} />,
    children: [
      { key: "invoices", label: "Invoices", icon: <FileCheck size={18} /> },
      { key: "payment", label: "Payment", icon: <CreditCard size={18} /> },
      { key: "commission", label: "Service Fee", icon: <Percent size={18} /> },
    ],
  },
  {
    key: "agreement",
    label: "Agreement",
    icon: <FileText size={18} />,
    children: [
      { key: "product_agreement", label: "Product Agreement", icon: <FileText size={18} /> },
      { key: "baa", label: "BAA", icon: <Files size={18} /> },
      { key: "medical_note_agreement", label: "Medical Note Agreement", icon: <ClipboardList size={18} /> },
    ],
  },
  { key: "templates", label: "Templates", icon: <DescriptionOutlined sx={{ fontSize: 18 }} /> },
  {
    key: "your_org",
    label: "Your Org",
    icon: <Building size={18} />,
    children: [
      { key: "user", label: "User", icon: <User size={18} /> },
      { key: "user_create", label: "User Create", icon: <UserPlus size={18} /> },
      { key: "practice", label: "Practice", icon: <Building size={18} /> },
      { key: "provider", label: "Provider", icon: <UserCheck size={18} /> },
      { key: "agency", label: "Agency", icon: <Building2 size={16} /> },
      { key: "manufacturer", label: "Manufacturer", icon: <Factory size={18} /> },
    ],
  },
  { key: "ai_medical_audit", label: "AI Medical Audit", icon: <BrainCog size={18} /> },
];

const sidebarBottom: SidebarItem[] = [
  { key: "resources", label: "Resources", icon: <FolderOpen size={18} /> },
  { key: "sign_out", label: "Sign Out", icon: <LogOut size={18} /> },
];

const SIDEBAR_WIDTH = 240;
const TOOLTIP_WIDTH = 310;
const TOOLTIP_GAP = 14;
const FOOTER_CLEARANCE = 70;

const childKeysOf = (item: SidebarItem) => (item.children ?? []).map((c) => c.key);

function SpotlightOverlay({
  itemRefs,
  activeKey,
}: {
  itemRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  activeKey: string;
}) {
  const [rect, setRect] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const updateRect = React.useCallback(() => {
    const el = itemRefs.current[activeKey];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 4;
    setRect({
      top: r.top - pad,
      left: r.left - pad,
      width: r.width + pad * 2,
      height: r.height + pad * 2,
    });
  }, [activeKey, itemRefs]);

  React.useEffect(() => {
    const t1 = setTimeout(updateRect, 60);
    const t2 = setTimeout(updateRect, 350);
    const t3 = setTimeout(updateRect, 550);
    window.addEventListener("resize", updateRect);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", updateRect);
    };
  }, [updateRect]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: "12px",
        zIndex: 1200,
        pointerEvents: "none",
        boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
        transition:
          "top 0.4s cubic-bezier(0.22,1,0.36,1), left 0.4s cubic-bezier(0.22,1,0.36,1), width 0.4s ease, height 0.4s ease",
      }}
    />
  );
}

export default function PlatformTour() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = React.useState(0);
  const [tooltipState, setTooltipState] = React.useState<"enter" | "exit">("enter");
  const [animKey, setAnimKey] = React.useState(0);
  const [tooltipTop, setTooltipTop] = React.useState(100);

  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const sidebarRef = React.useRef<HTMLDivElement | null>(null);

  const totalSteps = tourSteps.length;
  const step = tourSteps[currentStep];

  const autoExpanded = React.useMemo(() => {
    const menus: string[] = [];
    [...sidebarTop, ...sidebarBottom].forEach((item) => {
      if (childKeysOf(item).includes(step?.key)) menus.push(item.key);
    });
    return menus;
  }, [step]);

  const [manualExpanded, setManualExpanded] = React.useState<string[]>([]);

  React.useEffect(() => {
    setManualExpanded([]);
  }, [currentStep]);

  const allExpanded = React.useMemo(
    () => Array.from(new Set([...autoExpanded, ...manualExpanded])),
    [autoExpanded, manualExpanded]
  );

  const logoIconSrc =
    theme.palette.mode === "dark"
      ? branding?.logoIconWhite || branding?.logoWhite
      : branding?.logoIcon ||
        branding?.logo ||
        require("../../../branding-assets/v3biomed/logo-icon.png");

  const updateTooltipPosition = React.useCallback(() => {
    if (!step) return;
    const el = itemRefs.current[step.key];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "nearest" });

    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const tooltipH = tooltipRef.current?.offsetHeight || 220;

      let top = rect.top - 8;
      top = Math.max(12, top);
      const maxTop = viewportH - tooltipH - FOOTER_CLEARANCE;
      top = Math.min(top, maxTop);

      setTooltipTop(top);
    });
  }, [step]);

  React.useEffect(() => {
    const t1 = setTimeout(updateTooltipPosition, 80);
    const t2 = setTimeout(updateTooltipPosition, 350);
    const t3 = setTimeout(updateTooltipPosition, 550);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [currentStep, allExpanded, updateTooltipPosition]);

  React.useEffect(() => {
    window.addEventListener("resize", updateTooltipPosition);
    return () => window.removeEventListener("resize", updateTooltipPosition);
  }, [updateTooltipPosition]);

  const goToStep = (next: number) => {
    setTooltipState("exit");
    setTimeout(() => {
      setCurrentStep(next);
      setAnimKey((k) => k + 1);
      setTooltipState("enter");
    }, 200);
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) goToStep(currentStep + 1);
    else navigate("/select_care_categories");
  };
  const handleBack = () => {
    if (currentStep > 0) goToStep(currentStep - 1);
  };
  const handleSkip = () => navigate("/select_care_categories");

  const toggleMenu = (key: string) =>
    setManualExpanded((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const isHighlighted = (key: string) => step?.key === key;

  const renderItem = (
    item: SidebarItem | SidebarChild,
    indent = false,
    hasChildren = false
  ) => {
    const highlighted = isHighlighted(item.key);
    const isExpanded = allExpanded.includes(item.key);

    return (
      <Box
        ref={(el: HTMLDivElement | null) => {
          itemRefs.current[item.key] = el;
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 0.85,
          ml: indent ? 2.5 : 0,
          borderRadius: 1.5,
          cursor: "pointer",
          position: "relative",
          zIndex: highlighted ? 1250 : "auto",
          border: highlighted ? "1px solid #14b8a6" : "1px solid transparent",
          backgroundColor: highlighted ? alpha("#14b8a6", 0.1) : "transparent",
          transition: "all 0.3s ease",
          animation: "none",
          "&:hover": { backgroundColor: alpha("#14b8a6", 0.06) },
        }}
        onClick={() => hasChildren && toggleMenu(item.key)}
      >
        <Box
          sx={{
            color: highlighted ? "#14b8a6" : "#9ca3af",
            display: "flex",
            alignItems: "center",
            transition: "color 0.3s ease",
          }}
        >
          {item.icon}
        </Box>
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            color: highlighted ? "#fff" : "#cbd5e1",
            fontWeight: highlighted ? 600 : 400,
            fontSize: "0.82rem",
            transition: "all 0.3s ease",
          }}
        >
          {item.label}
        </Typography>
        {hasChildren && (
          <Box
            sx={{
              color: "#9ca3af",
              display: "flex",
              transition: "transform 0.3s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>
    );
  };

  const renderNode = (item: SidebarItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = allExpanded.includes(item.key);

    return (
      <Box key={item.key}>
        {renderItem(item, false, !!hasChildren)}

        {hasChildren && (
          <Box
            sx={{
              overflow: "hidden",
              maxHeight: isExpanded ? item.children!.length * 42 + 8 : 0,
              opacity: isExpanded ? 1 : 0,
              transition:
                "max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease",
            }}
          >
            {item.children!.map((child) => (
              <Box key={child.key} sx={{ mt: 0.25 }}>
                {renderItem(child, true, false)}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderTooltip = () => {
    if (!step) return null;

    return (
      <Box
        ref={tooltipRef}
        key={animKey}
        sx={{
          position: "fixed",
          left: SIDEBAR_WIDTH + TOOLTIP_GAP,
          top: tooltipTop,
          zIndex: 1300,
          width: TOOLTIP_WIDTH,
          animation:
            tooltipState === "enter"
              ? `${tooltipEnter} 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards`
              : `${tooltipExit} 0.18s ease forwards`,
          pointerEvents: tooltipState === "exit" ? "none" : "auto",
          transition: "top 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? "#1f2933" : "#fff",
            borderRadius: 3,
            boxShadow:
              "0 16px 48px rgba(0,0,0,0.3), 0 0 0 1px rgba(20,184,166,0.06)",
            p: 3,
            position: "relative",
            overflow: "hidden",
            border: 1,
            borderColor: colors.grey[700],
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="caption"
              sx={{ color: "#14b8a6", fontWeight: 600 }}
            >
              {currentStep + 1} of {totalSteps}
            </Typography>
            <IconButton
              size="small"
              onClick={handleSkip}
              sx={{
                color: "#64748b",
                width: 26,
                height: 26,
                "&:hover": {
                  color: "#94a3b8",
                  backgroundColor: alpha("#fff", 0.05),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Typography
            fontWeight={700}
            sx={{ color: colors.grey[100]}}
          >
            {step.title}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "#94a3b8", mb: 1, lineHeight: 1.65 }}
          >
            {step.description}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
              disabled={currentStep === 0}
              onClick={handleBack}
              sx={{
                textTransform: "none",
                borderColor: "#334155",
                color: "#cbd5e1",
                borderRadius: 1.5,
                px: 1.5,
                fontWeight: 600,
                fontSize: "0.8rem",
                "&:hover": {
                  borderColor: "#14b8a6",
                  backgroundColor: alpha("#14b8a6", 0.06),
                  color: "#14b8a6",
                },
                "&.Mui-disabled": { borderColor: "#1e293b", color: "#334155" },
              }}
            >
              Back
            </Button>

            <Button
              size="small"
              variant="text"
              onClick={handleSkip}
              sx={{
                textTransform: "none",
                color: "#64748b",
                fontSize: "0.8rem",
                fontWeight: 500,
                "&:hover": {
                  color: "#94a3b8",
                  backgroundColor: "transparent",
                },
              }}
            >
              Skip Tour
            </Button>

            <Button
              size="small"
              variant="contained"
              endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
              onClick={handleNext}
              sx={{
                textTransform: "none",
                backgroundColor: "#14b8a6",
                color: "#fff",
                fontWeight: 600,
                borderRadius: 1.5,
                px: 2,
                fontSize: "0.8rem",
                boxShadow: "0 4px 14px rgba(20,184,166,0.3)",
                "&:hover": {
                  backgroundColor: "#0d9488",
                  boxShadow: "0 6px 20px rgba(20,184,166,0.4)",
                },
              }}
            >
              {currentStep === totalSteps - 1 ? "Finish" : "Next"}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSignOut = () => {
    const highlighted = isHighlighted("sign_out");

    return (
      <Box
        ref={(el: HTMLDivElement | null) => {
          itemRefs.current["sign_out"] = el;
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 0.85,
          borderRadius: 1.5,
          cursor: "pointer",
          position: "relative",
          zIndex: highlighted ? 1250 : "auto",
          border: highlighted ? "1px solid #14b8a6" : "1px solid transparent",
          backgroundColor: highlighted
            ? alpha("#14b8a6", 0.1)
            : "transparent",
          transition: "all 0.3s ease",
          animation: "none",
          "&:hover": { backgroundColor: alpha("#ff4d4f", 0.06) },
        }}
      >
        <Box
          sx={{
            color: highlighted ? "#14b8a6" : "#ff4d4f",
            display: "flex",
            alignItems: "center",
          }}
        >
          <LogOut size={18} />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: highlighted ? "#fff" : "#ff4d4f",
            fontWeight: 600,
            fontSize: "0.82rem",
          }}
        >
          Sign Out
        </Typography>
      </Box>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                          */
  /* ---------------------------------------------------------------- */
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* ---- Sidebar ---- */}
      <Box
        ref={sidebarRef}
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          borderRight: `1px solid ${alpha("#334155", 0.4)}`,
          pt: 2,
          px: 1.5,
          pb: 0,
          zIndex: 5,
        }}
      >
        {/* Logo */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={2.5}
          px={1}
        >
          <Box
            component="img"
            src={logoIconSrc}
            alt="Logo"
            sx={{ height: 40, objectFit: "contain" }}
          />
        </Box>

        {/* Scrollable menu area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: alpha("#14b8a6", 0.2),
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: alpha("#14b8a6", 0.35),
            },
          }}
        >
          <Stack spacing={0.3}>
            {sidebarTop.map((item) => renderNode(item))}
          </Stack>
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${alpha("#334155", 0.4)}`,
            pt: 1,
            pb: 6,
            mt: 0.5,
          }}
        >
          <Stack spacing={0.3}>
            {sidebarBottom
              .filter((b) => b.key !== "sign_out")
              .map((item) => (
                <Box key={item.key}>{renderItem(item, false, false)}</Box>
              ))}
            {renderSignOut()}
          </Stack>
        </Box>
      </Box>

      {step && (
        <SpotlightOverlay itemRefs={itemRefs} activeKey={step.key} />
      )}

      {renderTooltip()}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 4,
          minWidth: 0,
        }}
      >
        <Box mb={4} sx={{ animation: `${fadeInUp} 0.6s ease 0.2s both` }}>
          <Box
            component="img"
            src={logoIconSrc}
            alt="V3 Biomed"
            sx={{ height: 56, objectFit: "contain" }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            mb: 3,
            animation: `${fadeInUp} 0.6s ease 0.35s both`,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" sx={{ color: "#9ca3af" }}>
              Step 3 of 3
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#14b8a6", fontWeight: 600 }}
            >
              60%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={60}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha("#14b8a6", 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                backgroundColor: "#14b8a6",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            animation: `${fadeInUp} 0.6s ease 0.5s both`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            fontWeight={600}
            sx={{ color: alpha("#9ca3af", 0.8), mb: 1 }}
          >
            Step 3: Platform Tour
          </Typography>
          <Typography sx={{ color: "#4b5563" }}>
            Let us show you around the platform. Follow the highlights to learn
            about each section.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}